import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const LeaderboardRankings = ({ leaderboardData, currentUser }) => {
  const [timeframe, setTimeframe] = useState('monthly');
  const [category, setCategory] = useState('earnings');

  const timeframes = [
    { value: 'weekly', label: 'This Week' },
    { value: 'monthly', label: 'This Month' },
    { value: 'quarterly', label: 'This Quarter' },
    { value: 'yearly', label: 'This Year' }
  ];

  const categories = [
    { value: 'earnings', label: 'Top Earners', icon: 'DollarSign' },
    { value: 'referrals', label: 'Most Referrals', icon: 'Users' },
    { value: 'success_rate', label: 'Success Rate', icon: 'Target' },
    { value: 'quality_score', label: 'Quality Score', icon: 'Star' }
  ];

  const getRankIcon = (rank) => {
    if (rank === 1) return { icon: 'Crown', color: 'text-yellow-500' };
    if (rank === 2) return { icon: 'Medal', color: 'text-gray-400' };
    if (rank === 3) return { icon: 'Award', color: 'text-amber-600' };
    return { icon: 'Hash', color: 'text-muted-foreground' };
  };

  const formatValue = (value, type) => {
    switch (type) {
      case 'earnings':
        return `$${value?.toLocaleString()}`;
      case 'referrals':
        return value?.toString();
      case 'success_rate':
        return `${value}%`;
      case 'quality_score':
        return `${value}/10`;
      default:
        return value?.toString();
    }
  };

  const currentUserRank = leaderboardData?.findIndex(user => user?.id === currentUser?.id) + 1;
  const currentUserData = leaderboardData?.find(user => user?.id === currentUser?.id);

  return (
    <div className="space-y-6">
      {/* Current User Position */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
              <Icon name="User" size={20} color="white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Your Current Position</h3>
              <p className="text-sm text-muted-foreground">
                {timeframes?.find(t => t?.value === timeframe)?.label} • {categories?.find(c => c?.value === category)?.label}
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name={getRankIcon(currentUserRank)?.icon} size={20} className={getRankIcon(currentUserRank)?.color} />
              <span className="text-2xl font-bold text-foreground">#{currentUserRank}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {formatValue(currentUserData?.[category] || 0, category)}
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">Timeframe</label>
            <div className="flex items-center space-x-2">
              {timeframes?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => setTimeframe(option?.value)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    timeframe === option?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {option?.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">Category</label>
            <div className="flex items-center space-x-2">
              {categories?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => setCategory(option?.value)}
                  className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    category === option?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  <Icon name={option?.icon} size={16} />
                  <span className="hidden sm:inline">{option?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm font-medium text-muted-foreground border-b border-border pb-2">
            <span>Rank</span>
            <span>Intermediary</span>
            <span>Performance</span>
            <span>Trend</span>
          </div>

          {leaderboardData?.slice(0, 10)?.map((user, index) => {
            const rank = index + 1;
            const rankInfo = getRankIcon(rank);
            const isCurrentUser = user?.id === currentUser?.id;
            
            return (
              <div 
                key={user?.id} 
                className={`flex items-center justify-between p-4 rounded-lg transition-all duration-200 ${
                  isCurrentUser 
                    ? 'bg-primary/10 border border-primary/20' :'bg-muted/30 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 w-12">
                    <Icon name={rankInfo?.icon} size={18} className={rankInfo?.color} />
                    <span className="font-medium text-foreground">{rank}</span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img 
                        src={user?.avatar} 
                        alt={user?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground flex items-center space-x-2">
                        <span>{user?.name}</span>
                        {isCurrentUser && (
                          <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{user?.location}</div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-foreground">
                    {formatValue(user?.[category], category)}
                  </div>
                  <div className="text-sm text-muted-foreground">{user?.subtitle}</div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center space-x-1 text-sm ${
                    user?.trend > 0 ? 'text-green-600' : user?.trend < 0 ? 'text-red-600' : 'text-muted-foreground'
                  }`}>
                    <Icon 
                      name={user?.trend > 0 ? 'TrendingUp' : user?.trend < 0 ? 'TrendingDown' : 'Minus'} 
                      size={16} 
                    />
                    <span>{user?.trend > 0 ? '+' : ''}{user?.trend}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View More */}
        <div className="flex items-center justify-center mt-6">
          <Button variant="outline">
            <Icon name="ChevronDown" size={16} className="mr-2" />
            View Full Leaderboard
          </Button>
        </div>
      </div>
      {/* Performance Insights */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Performance Insights</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Target" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">To Reach Top 10</span>
            </div>
            <p className="text-sm text-muted-foreground">
              You need {formatValue(2500, category)} more to enter the top 10 rankings.
            </p>
          </div>
          
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-accent" />
              <span className="text-sm font-medium text-foreground">Monthly Growth</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Your performance improved by 15% compared to last month.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardRankings;